package com.dotdrives.web.pages.nextsteps

import com.dotdrives.controller.AutomationRulesController
import com.dotdrives.controller.NewReminderController
import com.dotdrives.controller.NextStepsController
import com.dotdrives.controller.PipelineHealthController
import com.dotdrives.model.OrgMember
import com.dotdrives.repository.SupabaseAutomationRuleRepository
import com.dotdrives.repository.SupabasePipelineHealthRepository
import com.dotdrives.repository.SupabaseReminderRepository
import com.dotdrives.repository.SupabaseOrgRepository
import com.dotdrives.web.shell.NavItem
import com.dotdrives.web.shell.PortalShell
import com.dotdrives.web.ui.useFlowState
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.awaitCancellation
import kotlinx.coroutines.cancel
import kotlinx.coroutines.launch
import react.FC
import react.Props
import react.useEffect
import react.useState

private class NextStepsControllerHolder(
    val scope: kotlinx.coroutines.CoroutineScope,
    val nextSteps: NextStepsController,
    val health: PipelineHealthController,
    val automation: AutomationRulesController,
    val newReminder: NewReminderController,
    val orgRepository: SupabaseOrgRepository,
)

external interface NextStepsPortalProps : Props {
    var currentUserRole: String
    var orgId: String
    var userId: String
    var userInitials: String
    var userName: String
    var orgName: String
    var canWrite: Boolean
    var onSwitchOrg: () -> Unit
    var onNavigate: (NavItem) -> Unit
    var onSignOut: () -> Unit
}

val NextStepsPortal = FC<NextStepsPortalProps> { props ->
    val (holder) = useState {
        val scope = MainScope()
        val reminderRepo = SupabaseReminderRepository()
        val automationRepo = SupabaseAutomationRuleRepository()
        val healthRepo = SupabasePipelineHealthRepository()
        val orgRepository = SupabaseOrgRepository()
        NextStepsControllerHolder(
            scope = scope,
            nextSteps = NextStepsController(reminderRepo, scope, props.orgId, props.userId),
            health = PipelineHealthController(healthRepo, scope, props.orgId),
            automation = AutomationRulesController(automationRepo, scope, props.orgId),
            newReminder = NewReminderController(reminderRepo, scope, props.orgId, props.userId),
            orgRepository = orgRepository,
        )
    }
    val nextStepsState = useFlowState(holder.nextSteps.state)
    val healthState = useFlowState(holder.health.state)
    val automationState = useFlowState(holder.automation.state)
    val newReminderState = useFlowState(holder.newReminder.state)
    val (members, setMembers) = useState<List<OrgMember>>(emptyList())

    useEffect(holder) {
        holder.scope.launch {
            holder.orgRepository.members(props.orgId).onSuccess { setMembers(it) }
        }
        try {
            awaitCancellation()
        } finally {
            holder.scope.cancel()
        }
    }

    val delivery = com.dotdrives.web.ui.useWorkflowController(props.userId, props.orgId, props.currentUserRole)
    val deliveryState = useFlowState(delivery.state)
    useEffect(delivery) { delivery.loadJobs() }

    PortalShell {
        active = NavItem.NextSteps
        onNavigate = props.onNavigate
        userInitials = props.userInitials
        userName = props.userName
        orgName = props.orgName
        onSwitchOrg = props.onSwitchOrg
        onSignOut = props.onSignOut

        NextStepsPage {
            canAdminister = com.dotdrives.model.canAdminister(props.currentUserRole)
            canWriteReminders = props.canWrite && props.currentUserRole in setOf("owner", "admin", "member")
            nextStepsController = holder.nextSteps
            this.nextStepsState = nextStepsState
            healthController = holder.health
            this.healthState = healthState
            automationController = holder.automation
            this.automationState = automationState
            newReminderController = holder.newReminder
            this.newReminderState = newReminderState
            this.members = members
            currentUserId = props.userId
            currentUserName = props.userName
        }
        if (props.currentUserRole != "field") {
            com.dotdrives.web.ui.WorkflowFeedback { controller = delivery; state = deliveryState }
            com.dotdrives.web.ui.DeliveryJobs { controller = delivery; state = deliveryState; sourceId = null; kinds = listOf("email.reminder") }
        }
    }
}
